import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Tool, Asset, ToolCategory, ToolSubcategory, ToolDeliverable, ToolParameter, Attachment, ToolFilter, GeneralSettings, ToolLocationOrigin, AddressLocation, ToolProcessFlow, ToolAsset, PartSupplierDetails, AssociatedPart, ToolParameterType, AssetMismatch, ToolDeliverableStatus } from '../../../models/generic.model';
import { ProfileService } from '../../../services/profile.service';
import { TslOption, TslSelectChange, TslDialog, TslSelect, TslFileDrop } from '@tesla/ng-tds';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, startWith, map } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ComponentType } from '@angular/cdk/portal';
import { I18nService } from 'app/shared/services/i18n.service';
import * as FileSaver from 'file-saver';
import { StateService } from 'app/profile/services/state.service';
import { StatusEnum } from 'app/profile/models/state.model';
import { MatDialog } from '@angular/material/dialog';
import { ParameterFormDialogComponent } from '../parameter-form-dialog/parameter-form-dialog.component';
import { DeliverableFormDialogComponent } from '../deliverable-form-dialog/deliverable-form-dialog.component';
import { CommentDataDialog } from '../comment-data-dialog/comment-data-dialog.component'
import { CustomValidator } from 'app/shared/custom-validator/custom-validator';
import { getGeneralSettings, SelectCurrentUser, SelectToolCategories, SelectToolSubCategories, SelectSupplierLocations } from 'app/profile/store/app.selector';
import { GeneralSettingsState } from 'app/profile/store/state/general.state';
import { Store, select } from '@ngrx/store';
import { PeopleService } from 'app/profile/services/people.service';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { GetSupplierProfileCurrentUser, GetToolCategories, GetToolSubCategories, GetSupplierLocationsByCode } from 'app/profile/store/app.actions';
import { UserDetails } from 'app/profile/models/user.model';
import { AssociatedPartsFormDialogComponent } from '../associated-parts-form-dialog/associated-parts-form-dialog.component';
import { ParameterTypeDialogComponent } from '../parameter-type-dialog/parameter-type-dialog.component';
import { expendableFieldValidator } from 'app/shared/directive/expendable-field.directive';
import { HostListener } from '@angular/core';
import { AssetMismatchComponent } from './asset-mismatch/asset-mismatch.component';
import { AssetStatusHelpContent } from '../asset-status-help-content.component';
import { StatusHelpContent } from '../status-help-content.component';
import { OverrideCommentDialog } from '../override-comment-dialog/override-comment-dialog.component';
import { I } from '@angular/cdk/keycodes';
import { AppSettings } from '../../../../app.settings';
import { ReorderDeliverablesDialog } from '../reorder-deliverables-dialog/reorder-deliverables-dialog.component';

@Component({
  selector: 'tool-form',
  templateUrl: './tool-equipment-form.component.html',
  styleUrls: ['./tool-equipment-form.component.scss']
})
export class ToolEquipmentFormComponent implements OnInit {
  @ViewChild('tagFileDrop', {static: false}) tagFileDrop: TslFileDrop;
  @ViewChild('assetFileDrop', {static: false}) assetFileDrop: TslFileDrop;
  
  @Input() isEquipment = true;
  @Input() currentTool: Tool = new Tool();
  @Output() close = new EventEmitter<boolean>();
  settings: GeneralSettings;
  deliverableColumns: string[] = ['event', 'deliverable', 'startDate', 'plannedDate', 'actualDate', 'files', 'submissionInfo', 'reviewInfo', 'status', 'actions'];

  categoryRef: TslSelect;
  subCategoryRef: TslSelect;
  deleteTitle: string;
  deleteConfirmation: string;
  duplicateAssetTitle: string;
  duplicateAssetMessage: string;
  submissionMessage: string;
  statusChange: boolean;

  assetList: Asset[]; // all available assets
  allAssetList: Asset[] = []; // all assets
  assetSelected: ToolAsset[] = [];
  assetsFiltered$: Observable<any>;
  filterCategories: ToolCategory[];
  filterSubcategories: ToolSubcategory[];
  allSubcategories: ToolSubcategory[];
  deliverablesData: (ToolDeliverable | any)[] = [];
  parametersData: (ToolParameter | any)[];
  locations: AddressLocation[] = [];
  locationsFiltered$: Observable<AddressLocation[]>;
  toolGroup: FormGroup;
  processFlows: ToolProcessFlow[] = [];
  defaultParameters: (ToolParameterType | any)[] = [];
  defaultDeliverables: (ToolDeliverable | any)[] = [];

  associatedPartsColumns: string[] = ['PartNumber', 'PartDescription', 'Asset', 'DemandType', 'ProcessName', 'ProcessStep', 'AssociatedRequirement', 'MistakePrevention'];
  associatedPartsData: (AssociatedPart | any)[] = [];

  isAssetTagNumberAdding: boolean = false;
  savedAssets: number;
  errortable: AssetMismatch[] = [];
  showCMTChanges: any;
  hasAssetStatusError: boolean = false;
  toolSelected: Tool = new Tool();
  submitDate: string;
  datePipe: DatePipe;
  currentAssociatedParts: AssociatedPart[];

  get fileSizeLimitMB() { return this.settings.fileSizeLimitMB * 1000000; }

  emails: string[] = [];
  userDetails: UserDetails;
  allowEdit: boolean = false;
  allowEditTeslaUsers: boolean = false;
  del: ToolDeliverable[] = [];
  locationID: number[] = [];
  assetStatusHelpContent: ComponentType<AssetStatusHelpContent> = AssetStatusHelpContent;
  statusHelpContent: ComponentType<StatusHelpContent> = StatusHelpContent;

  // getters
  get role() { return this.isEquipment ? this.translate.get("Equipment") : this.translate.get("Tool"); }
  get isTeslaAsset() { return this.toolGroup.get('isTeslaAsset'); }
  get assetTagNumber() { return this.toolGroup.get('assetTagNumber'); }
  get toolCategory() { return this.toolGroup.get('toolCategory'); }
  get toolSubcategory() { return this.toolGroup.get('toolSubcategory'); }
  get name() { return this.toolGroup.get('name'); }
  get identifier() { return this.toolGroup.get('identifier'); }
  get status() { return this.toolGroup.get('status'); } // tool asset
  get assets() { return this.toolGroup.get('assets') as FormArray; } // tesla assets
  get location() { return this.toolGroup.get('location'); }
  get isExpendable() { return this.toolGroup.get('isExpendable'); }
  get hasOverrideComment() { return this.currentTool.data.statusMismatchOverrideComment; }
  get hasNoAssociatedParts() { return !this.currentTool.data.associatedParts || this.currentTool.data.associatedParts.length === 0; }
  get hasIncorrectAddress() {
    if (!this.locationID.includes(this.currentTool.data.locationId) || this.location.value.length == 0) {
      this.location.setErrors({ 'incorrect': true });
      return true;
    } else {
      this.location.setErrors(null);
      return false;
    }
  }
  get hastoolCategory() {
    if (this.toolCategory.value) {
      return true;
    }
    return false;
  }

  constructor(
    private fb: FormBuilder,
    private dialog: TslDialog,
    private stateService: StateService,
    private translate: I18nService,
    private service: ProfileService,
    private peopleService: PeopleService,
    private store: Store<GeneralSettingsState>,
    private supplierStore: Store<SupplierState>,
    private configuration: AppSettings,
    public matDialog: MatDialog) {
    this.createForms();
  }

  ngOnInit() {
    this.supplierStore.dispatch(new GetSupplierProfileCurrentUser());

    this.supplierStore.pipe(select(SelectCurrentUser)).subscribe(result => {
      if (result) {
        this.store.select(getGeneralSettings).subscribe(res => this.settings = res);
        this.showCMTChanges = this.settings.settings.showCMTChanges;
        this.userDetails = result;
        this.allowEdit = this.userDetails.isSupplier || (this.userDetails.isTeslaUser && this.userDetails.isSIE);
        this.allowEditTeslaUsers = (this.userDetails.isTeslaUser && this.userDetails.isSIE);
        this.loadResources();
        this.setCurrentTool();
        this.currentAssociatedParts = this.associatedPartsData;
      }
    });
  }

  onSave(): void {
    this.assets.markAsTouched();
    if (this.toolGroup.invalid) {
      return;
    }

    if (this.showCMTChanges == "1") {
      this.setStatusForTeslaAsset();
    }

    // fill model
    this.currentTool.data.isTeslaAsset = this.isTeslaAsset.value;
    this.currentTool.toolCategoryId = this.toolGroup.value.toolCategory;
    this.currentTool.toolSubcategoryId = this.toolGroup.value.toolSubcategory;
    this.currentTool.data.identifier = this.toolGroup.value.identifier;

    // If isExpendable is undefined the set as false
    if (this.isExpendable) {
      this.currentTool.data.isExpendable = !this.toolGroup.value.isExpendable ? false : true;
      this.currentTool.data.numberOfParts = this.toolGroup.value.numberOfParts;
      this.currentTool.data.expirationDate = this.toolGroup.value.expirationDate;
    }
    else {
      this.currentTool.data.isExpendable = false;
      this.currentTool.data.numberOfParts = null;
      this.currentTool.data.expirationDate = null;
    }

    this.currentTool.data.quantity = !this.toolGroup.value.quantity ? 0 : this.toolGroup.value.quantity;
    this.currentTool.data.name = this.toolGroup.value.name;
    this.currentTool.data.status = this.toolGroup.value.status;
    this.currentTool.data.description = this.toolGroup.value.description;
    this.currentTool.data.assets = this.assets.value;

    if (this.currentTool.data.isTeslaAsset == true && (!this.currentTool.data.locationId ||
      (this.currentTool.data.locationId && this.locations.filter(x => x.id == this.currentTool.data.locationId).length == 0))) {
      this.dialog.alert(this.translate.get('Error'), this.translate.get('Please select location for the tool.'));
      return;
    }

    // Check asset Status is valid based on below logic.
    // Asset Status Active: - Get the list of part associated with Tool
    // 1. Get part associated with Asset from asset maintenance system
    // 2. Get part from Associated parts tab where user mannually added parts
    // 3. Get Part from process flow associated with Tool.
    // Get distinct Parts and check if any part has Demand Type "Production"
    // Asset Status Future/Service then no validation needed.
    // Let's do this validation in API only.
    let assetToolData = this.assets.value as ToolAsset[];

    // Remove duplicates
    const result = [];
    const map = new Map();
    for (const item of this.currentTool.data.associatedParts) {
      if (!map.has(item.partNumber)) {
        map.set(item.partNumber, true);
        result.push({
          partNumber: item.partNumber,
          partDescription: item.partDescription,
          partId: item.partId
        });
      }
    }
    this.currentTool.data.associatedParts = result;

    if (this.showCMTChanges == "0" && assetToolData.some(x => x.assetStatus.toLocaleLowerCase() == "active")) {
      if (this.processFlows && !this.processFlows.some(x => x.demandType && x.demandType.toLocaleLowerCase() == "production demand")) {

        if (!this.currentTool.data.associatedParts) {
          this.currentTool.data.associatedParts = [];
        }

        let partNumbers: string[] = [];
        partNumbers = partNumbers.concat(...this.currentTool.data.associatedParts.map(x => x.partNumber));
        let parts = this.allAssetList.filter(x => assetToolData.some(y => y.assetId == x.assetId)).map(asset => asset.partNumbers);
        parts.forEach(x => {
          partNumbers = partNumbers.concat(...x.split(','));
        });

        partNumbers = partNumbers.filter(x => x && x != '');

        // if associated parts are present, verify asset mismatch and save the data, else throw an error.
        if (partNumbers.length > 0) {
          this.service.getPartSupplierDetails(this.currentTool.supplierCode, partNumbers).subscribe((res: PartSupplierDetails[]) => {
            if (!res.some(x => x.demandType && x.demandType.toLocaleLowerCase() == "production demand")) {
              this.dialog.alert(this.translate.get('Error'), this.translate.get('Cannot set asset status active because part doesnot have production demand.'));
            }
            else {
              this.saveData();
            }
          });
        }
        else {
          this.dialog.alert(this.translate.get('Error'), this.translate.get('Please add part to set asset status active.'));
        }
      }
    }
    else if (this.showCMTChanges == "1" && this.currentTool.data.isTeslaAsset == true) {
      if (!this.checkForAnyOpenCases()) {
        this.verifyAssetMismatchOnSave();
        this.hasAssetStatusError = false;
      }
    }
    else {
      this.saveData();
    }
  }

  getStatus(status: number) {
    if (status == 4) {
      return "Not Submitted";
    } else {
      return ToolDeliverableStatus[status]
    }
  }

  getStatusColor(status: number) {
    let statusCssClass = "";
    if (status == ToolDeliverableStatus.Submitted) {
      statusCssClass = "status-Submitted";
      return ["chipstatus", statusCssClass];
    }
    if (status == ToolDeliverableStatus.Rejected) {
      statusCssClass = "status-Rejected";
      return ["chipstatus", statusCssClass];
    }
    if (status == ToolDeliverableStatus.Approved) {
      statusCssClass = "status-Approved";
      return ["chipstatus", statusCssClass];
    }
    if (status == ToolDeliverableStatus.NotSubmitted) {
      statusCssClass = "status-NotSubmitted";
      return ["chipstatus", statusCssClass];
    }
  }

  editOn(status: number): boolean {
    if (status == ToolDeliverableStatus.Rejected) {
      return true;
    }
    if (!this.userDetails.isSIE) {
      if (status == ToolDeliverableStatus.NotSubmitted) {
        return true;
      }
    } else {
      if (status != ToolDeliverableStatus.Approved) {
        return true;
      }
    }
    return false;
  }

  deleteOn(...state: any[]): boolean {
    if (this.userDetails.isSupplier && state[2]) {
      return false;
    }
    if (state[1]) {
      return false;
    }
    if (state[0] == ToolDeliverableStatus.Rejected && this.userDetails.isSIE) {
      return true;
    }
    if (state[0] == ToolDeliverableStatus.NotSubmitted) {
      return true;
    }
    return false;
  }

  saveData(): void {
    this.onSaveDeliverables();

    if (this.currentTool.id && this.currentTool.id > 0) {
      this.service.putTool(this.currentTool.supplierCode, this.currentTool)
        .subscribe(res => {
          if (res.id !== 0) {
            this.stateService.currentStatus.next({ type: StatusEnum.Success, message: '' });
            this.closeView(true);
          }
        });
    } else {
      this.service.postTool(this.currentTool.supplierCode, this.currentTool)
        .subscribe(res => {
          if (res.id !== 0) {
            this.stateService.currentStatus.next({ type: StatusEnum.Success, message: '' });
            this.closeView(true);
          }
        });
    }
  }

  getMismatchOverrideComment(): void {
    this.dialog.alert('Override Comment', this.currentTool.data.statusMismatchOverrideComment);
  }

  closeView(isSaved: boolean): void {
    // before saving; check for changes params, form data, deliverables and parts data
    if (!isSaved) {
      if (!this.currentTool.id) {
        this.currentTool.data.status = '';
      }
      else if (this.currentTool.id) {
        this.currentTool.data.status = this.toolSelected.data.status;
      }
      if ((this.toolGroup.dirty && this.toolGroup.touched) || this.paramsDirty() || this.unsavedDeliverables()) {
        this.dialog.confirm(this.translate.get('Unsaved Changes'), this.translate.get('You are about to leave this page. All unsaved changes will be lost. Are you sure you want to leave?'), this.translate.get('Continue'), this.translate.get('Cancel'))
          .afterClosed().subscribe((x: boolean) => {
            if (x) {
              this.close.emit(isSaved);
            }
          });
      }
      else {
        this.close.emit(isSaved);
      }
    }
    // close after saved
    else {
      this.close.emit(isSaved);
    }
  }

  showSubmissionInfo(...subInfo: any[]): void {

    //this.submitDate = this.datePipe.transform(subInfo[1], 'MMM d, y');

    this.submissionMessage = 'Submitter: ' + subInfo[0] +
      '\nSubmission Date: ' + subInfo[1] + '\nSubmission Comment: ' + subInfo[2];
    this.dialog.alert('Submission Info', this.submissionMessage);

  }

  showReviewInfo(...reviewInfo: any[]): void {

    //this.submitDate = subInfo[1];

    this.submissionMessage = 'Reviewer: ' + reviewInfo[0] +
      '\nReview Date: ' + reviewInfo[1] + '\nReview Comment: ' + reviewInfo[2];
    this.dialog.alert('Submission Info', this.submissionMessage);

  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    if ((this.toolGroup.dirty && this.toolGroup.touched) || this.paramsDirty() || this.unsavedDeliverables()) {
      return false;
    }
    return true;
  }

  paramsDirty(): boolean {
    var isDirty = false;
    var items = [];
    if (this.parametersData && this.parametersData.length > 0) {
      this.parametersData.forEach(x => {
        items.push({ typeId: x.typeId, typeName: x.typeName, value: x.value });
      });
    }
    if (this.currentTool.data.parameter && items) {
      if (JSON.stringify(this.currentTool.data.parameter) === JSON.stringify(items)) {
        isDirty = false;
      } else {
        isDirty = true;
      }
      return isDirty;
    }
  }

  unsavedDeliverables(): boolean {
    var isDirty = false;
    // get the deliverable data other than groups.
    var items = this.deliverablesData.filter(d => d.isGroup === undefined);
    // check for type id's 0, for unsaved - new (create/edit)
    if (this.deliverablesData && items.length > 0) {
      var del = this.deliverablesData.filter(d => d.isGroup === undefined && d.deliverableTypeId == 0);
      if (del && del.length > 0) {
        isDirty = true;
      }
      // compare with old object to detect the changes
      else if (JSON.stringify(this.del) !== JSON.stringify(items)) {
        isDirty = true;
      }
    }
    return isDirty;
  }

  onIsTeslaAsset(): void {
    if (this.isTeslaAsset.value) {
      this.assetTagNumber.enable();
      this.status.disable();
      this.assets.setValidators(CustomValidator.arrayRequiredValidator);
    } else {
      this.assetTagNumber.disable();
      this.status.enable();
      this.assets.clearValidators();
    }
    this.assets.updateValueAndValidity();
    this.assets.markAsDirty();
  }

  onSelectLocation(option: TslOption): void {
    const selected: AddressLocation = option.value;
    this.currentTool.data.locationId = selected.id;
    this.toolGroup.controls.location.setValue(selected.name + " " + selected.address1 + " " + selected.city + " " + selected.state + " " + selected.zipcode + " " + selected.country);
  }

  /**
* on selecting a Tesla asset.
*/
  onSelectAsset(option: TslOption): void {
    const filterRequest = new ToolFilter();
    const selected: Asset = option.value;
    filterRequest.assetId = selected.assetId;
    filterRequest.take = 1;

    this.service.searchTools(this.currentTool.supplierCode, filterRequest)
      .subscribe(res => {
        if (res.totalRecords > 0) {
          this.dialog.confirm(this.translate.get(this.duplicateAssetTitle), this.translate.get(this.duplicateAssetMessage))
            .afterClosed()
            .subscribe(confirmed => {
              if (confirmed) {
                this.onAddSelectedAsset(selected);
                this.assetTagNumber.setValue('');
              }
              this.assetTagNumber.setValue('');
              this.isAssetTagNumberAdding = false;


            });
        } else {
          this.onAddSelectedAsset(selected);
          this.assetTagNumber.setValue('');
          this.isAssetTagNumberAdding = false;
        }
      });

  }

  /**
 * On adding a Tesla Asset Tag.
 */
  onAssetTagNumerAdding() {
    this.isAssetTagNumberAdding = true;
  }

  /**
* On removing a Tesla Asset Tag.
*/
  onRemoveAsset(assetCode: string, assetIndex: number): void {
    const index = this.assetSelected.findIndex(x => x.assetCode == assetCode);
    if (index > -1) {
      this.assetSelected.splice(index, 1);
      this.assets.removeAt(assetIndex);
      let assetData = this.allAssetList.find(x => x.assetCode == assetCode);
      this.assetList.push(assetData);
    }

    this.assets.updateValueAndValidity();
    this.assets.markAsDirty();
  }

  onChangeCategory(event: TslSelectChange): void {
    const categoryId = parseInt(event.value || 0, 0);
    this.filterSubcategories = JSON.parse(JSON.stringify(this.allSubcategories.filter(x => x.toolCategoryId === categoryId || categoryId === 0)));
    this.toolGroup.patchValue({
      toolSubcategory: this.currentTool.toolSubcategoryId
    });

    if (this.currentTool.toolSubcategoryId > 0) {
      this.onChangeSubcategory({ source: this.subCategoryRef, value: this.currentTool.toolSubcategoryId });
    }
  }

  getCMTNumber(assetCode: string) {
    // find the cmt# from the asset list in the tool:
    var matchingasset = this.assets.value.find(x => x.assetCode == assetCode);
    if (matchingasset != null && matchingasset != undefined && matchingasset.cmtNumber != null && matchingasset.cmtNumber != undefined) {
      return matchingasset.cmtNumber;
    }
    else {
      // find the cmt# in the asset list.
      if (this.allAssetList != null) {
        var getmatchingasset = this.allAssetList.find(x => x.assetCode == assetCode);
        if (getmatchingasset != null && getmatchingasset != undefined) {
          return getmatchingasset.cmtNumber;
        }
        else {
          return "";
        }
      }
    }
  }

  getCMTStatus(assetCode: string) {
    // find the cmt# from the asset list in the tool:
    var matchingasset = this.assets.value.find(x => x.assetCode == assetCode);
    if (matchingasset != null && matchingasset != undefined && matchingasset.cmtStatus != null && matchingasset.cmtStatus != undefined) {
      return matchingasset.cmtStatus;
    }
    else {
      // find the cmt# in the asset list.
      if (this.allAssetList != null) {
        var getmatchingasset = this.allAssetList.find(x => x.assetCode == assetCode);
        if (getmatchingasset != null && getmatchingasset != undefined) {
          return getmatchingasset.cmtStatus;
        }
        else {
          return '';
        }
      }
    }
  }

  getcmtUrl(assetCode: string) {
    if (this.configuration.Production) {
      return "https://tradepartnertickets.tesla.com/assetmaintenance/page/cases/assetstatusmismatch/" + this.getCMTNumber(assetCode);
    }
    else {
      return "https://sandbox-workbench-ext.tesla.com/assetmaintenance/page/cases/assetstatusmismatch/" + this.getCMTNumber(assetCode);
    }
  }

  /**
   * Gets partnumbers, demand types for each asset and set form flags for tesla asset dropdown.
   */
  setStatusForTeslaAsset() {
    // get the partnumbers associated with this asset:
    if (this.showCMTChanges == "1") {
      this.assets.value.forEach(element => {
        var matchingAsset = this.assetList.find(x => x.assetCode == element.assetCode);
        if (matchingAsset != null && matchingAsset != undefined) {
          element.cmtNumber = matchingAsset.cmtNumber;
          element.createCMT = matchingAsset.createCMT;
        }
        if (!this.hasNoAssociatedParts) {
          for (let i = 0; i < this.currentTool.data.associatedParts.length; i++) {
            element.partNumbers += this.currentTool.data.associatedParts[i].partNumber + ",";
            element.demandTypes += this.currentTool.data.associatedParts[i].demandType + ",";
          }
        }

        if (!element.demandTypes) {
          element.demandTypes = " ";
        }
      });
    }

    // get all assets with parts in production demand:
    if (this.associatedPartsData.length > 0) {

      // get all assets with parts in no demand:
      var partsWithNoDemand = this.associatedPartsData.filter(x => x.demandType && x.demandType.toLocaleLowerCase() == "no demand" && x.assets != "" && x.assets != null && x.assets != undefined);
      var noDemandAssets: string[] = [];
      var allNoDemAssets = Array.prototype.map.call(partsWithNoDemand, function (item) { return item.assets.replace(/\s*,\s*/g, ","); }).join(",");
      noDemandAssets = allNoDemAssets.split(',');
      noDemandAssets = [... new Set(noDemandAssets)]

      noDemandAssets.forEach(x => {
        if (this.assets.value.find(y => y.assetCode == x)) {
          this.assets.value.find(y => y.assetCode == x).hasNoDemand = true;
          this.assets.value.find(y => y.assetCode == x).showActiveStatus = false;
          this.assets.value.find(y => y.assetCode == x).showFutureStatus = false;
          this.assets.value.find(y => y.assetCode == x).showServiceStatus = false;
          this.assets.value.find(y => y.assetCode == x).showNoDemandStatus = true;
          this.assets.value.find(y => y.assetCode == x).partNumbers = this.findPartsAssociatedWithEachTeslaAsset(noDemandAssets, this.associatedPartsData).get(x);
          this.assets.value.find(y => y.assetCode == x).demandTypes = this.findDemandAssociatedWithEachTeslaAsset(noDemandAssets, this.associatedPartsData).get(x);
        }
      });

      var partsWithProdDemand = this.associatedPartsData.filter(x => x.demandType && x.demandType.toLocaleLowerCase() == "production demand" && x.assets != "" && x.assets != null && x.assets != undefined);
      var prodAssets: string[] = [];
      var allProdAssets = Array.prototype.map.call(partsWithProdDemand, function (item) { return item.assets.replace(/\s*,\s*/g, ","); }).join(",");
      prodAssets = allProdAssets.split(',');
      prodAssets = [... new Set(prodAssets)]

      // If any part in the asset has a production demand then, then asset status can be Active, Future, or Service no CMT created
      prodAssets.forEach(x => {
        if (this.assets.value.find(y => y.assetCode == x)) {
          this.assets.value.find(y => y.assetCode == x).hasProductionDemand = true;
          this.assets.value.find(y => y.assetCode == x).showActiveStatus = true;
          this.assets.value.find(y => y.assetCode == x).showFutureStatus = true;
          this.assets.value.find(y => y.assetCode == x).showServiceStatus = true;
          this.assets.value.find(y => y.assetCode == x).showNoDemandStatus = true;
          this.assets.value.find(y => y.assetCode == x).partNumbers = this.findPartsAssociatedWithEachTeslaAsset(prodAssets, this.associatedPartsData).get(x);
          this.assets.value.find(y => y.assetCode == x).demandTypes = this.findDemandAssociatedWithEachTeslaAsset(prodAssets, this.associatedPartsData).get(x);
        }
      });

      // get all assets with parts in future demand:
      var partsWithFutureDemand = this.associatedPartsData.filter(x => x.demandType && (x.demandType.toLocaleLowerCase() == "future production demand" || x.demandType.toLocaleLowerCase() == "future service demand") && x.assets != "" && x.assets != null && x.assets != undefined);
      // var futureAssets = x1.map(y => y.assets.split(','));
      var futureAssets: string[] = [];
      var allFutAssets = Array.prototype.map.call(partsWithFutureDemand, function (item) { return item.assets.replace(/\s*,\s*/g, ","); }).join(",");
      futureAssets = allFutAssets.split(',');
      futureAssets = [... new Set(futureAssets)]

      // If any part in the asset has a future demand then, then asset status can be Active, Future, or Service no CMT created
      futureAssets.forEach(x => {
        if (this.assets.value.find(y => y.assetCode == x)) {
          this.assets.value.find(y => y.assetCode == x).hasFutureDemand = true;
          this.assets.value.find(y => y.assetCode == x).showActiveStatus = true;
          this.assets.value.find(y => y.assetCode == x).showFutureStatus = true;
          this.assets.value.find(y => y.assetCode == x).showServiceStatus = true;
          this.assets.value.find(y => y.assetCode == x).showNoDemandStatus = true;
          this.assets.value.find(y => y.assetCode == x).partNumbers = this.findPartsAssociatedWithEachTeslaAsset(futureAssets, this.associatedPartsData).get(x);
          this.assets.value.find(y => y.assetCode == x).demandTypes = this.findDemandAssociatedWithEachTeslaAsset(futureAssets, this.associatedPartsData).get(x);
        }
      });

      // get all assets with parts in srvice demand:
      var partsWithServiceDemand = this.associatedPartsData.filter(x => x.demandType && x.demandType.toLocaleLowerCase() == "service demand" && x.assets != "" && x.assets != null && x.assets != undefined);
      // var serviceAssets = x2.map(y => y.assets.split(','));
      var serviceAssets: string[] = [];
      var allServAssets = Array.prototype.map.call(partsWithServiceDemand, function (item) { return item.assets.replace(/\s*,\s*/g, ","); }).join(",");
      serviceAssets = allServAssets.split(',');
      serviceAssets = [... new Set(serviceAssets)]

      // If any part in the asset has a service demand then, then asset status can be Active, Future, or Service no CMT created
      serviceAssets.forEach(x => {
        if (this.assets.value.find(y => y.assetCode == x)) {
          this.assets.value.find(y => y.assetCode == x).hasServiceDemand = true;
          this.assets.value.find(y => y.assetCode == x).showActiveStatus = true;
          this.assets.value.find(y => y.assetCode == x).showFutureStatus = true;
          this.assets.value.find(y => y.assetCode == x).showServiceStatus = true;
          this.assets.value.find(y => y.assetCode == x).showNoDemandStatus = true;
          this.assets.value.find(y => y.assetCode == x).partNumbers = this.findPartsAssociatedWithEachTeslaAsset(serviceAssets, this.associatedPartsData).get(x);
          this.assets.value.find(y => y.assetCode == x).demandTypes = this.findDemandAssociatedWithEachTeslaAsset(serviceAssets, this.associatedPartsData).get(x);
        }
      });
    }
    this.assets.updateValueAndValidity();
    this.currentTool.data.assets = this.assets.value;
    if (this.isTeslaAsset.value == true) {
      this.toolGroup.setControl('assets', this.fb.array(this.populateTeslaAssetsControl(), CustomValidator.arrayRequiredValidator));
    }
    else {
      this.toolGroup.setControl('assets', this.fb.array(this.populateTeslaAssetsControl()));
    }
  }

  /**
  * Gets partnumbers, demand types for each asset and set form flags for tesla asset dropdown.
  */
  setAssetStatusForTeslaAsset(assetCode: string, assetStatus: string) {

    // get all assets with parts in production demand:
    var assetSelected = this.assets.value.filter(x => x.assetCode == assetCode);

    // If any part in the asset has a production demand then, then asset status can be Active, Future, or Service no CMT created
    assetSelected.forEach(x => {
      if (this.assets.value.find(y => y.assetCode == x.assetCode)) {
        this.assets.value.find(y => y.assetCode == x.assetCode).assetStatus = assetStatus;
      }
    });

    this.changeToolAssetValue();
    this.assets.updateValueAndValidity();
  }

  /**
   * Gets associated part numbers with each Tesla Asset tag.
   */
  findPartsAssociatedWithEachTeslaAsset(assets: string[], partData: any[]) {
    var assetPartMapping = new Map<string, string>();
    assets.forEach(asset => {
      var pns = "";
      partData.forEach(part => {
        if (part.assets != "" && part.assets != null && part.assets != undefined && part.assets.indexOf(asset) != -1) {
          pns = pns + part.partNumber + ",";
        }
      });
      assetPartMapping.set(asset, pns);
    });
    return assetPartMapping;
  }

  /**
  * Gets associated demand types with each Tesla Asset tag.
  */
  findDemandAssociatedWithEachTeslaAsset(assets: string[], partData: any[]) {
    var assetDemandMapping = new Map<string, string>();
    assets.forEach(asset => {
      var dns = "";
      partData.forEach(part => {
        if (part.assets != "" && part.assets != null && part.assets != undefined && part.assets.indexOf(asset) != -1) {
          dns = dns + part.demandType + ",";
        }
      });
      assetDemandMapping.set(asset, dns);
    });
    return assetDemandMapping;
  }

  private populateTeslaAssetsControl(): FormGroup[] {
    if (this.showCMTChanges == "1") {
      return this.assets.value.map(x => {
        return this.fb.group({
          assetCode: [x.assetCode, Validators.required],
          assetId: [x.assetId, Validators.required],
          assetDescription: [x.assetDescription, Validators.required],
          assetStatus: [x.assetStatus, Validators.required],
          hasProductionDemand: [x.hasProductionDemand, []],
          hasFutureDemand: [x.hasFutureDemand, []],
          hasServiceDemand: [x.hasServiceDemand, []],
          hasNoDemand: [x.hasNoDemand, []],
          showActiveStatus: [x.showActiveStatus, []],
          showFutureStatus: [x.showFutureStatus, []],
          showServiceStatus: [x.showServiceStatus, []],
          showNoDemandStatus: [x.showNoDemandStatus, []],
          partNumbers: [x.partNumbers, Validators.required],
          demandTypes: [x.demandTypes, Validators.required],
          createCMT: [x.createCMT, []],
          cmtNumber: [x.cmtNumber, []],
          cmtStatus: [x.cmtStatus, []],
        })
      });
    }
    else {
      return this.assets.value.map(x => {
        return this.fb.group({
          assetCode: [x.assetCode, Validators.required],
          assetId: [x.assetId, Validators.required],
          assetDescription: [x.assetDescription, Validators.required],
          assetStatus: [x.assetStatus, Validators.required]
        })
      });
    }
  }

  /**
   * On selecting a tesla asset tag.
   */
  onSelectAssetStatus(asset: any, event: any) {

    this.setAssetStatusForTeslaAsset(asset.value.assetCode, event.value);

    // if there exists an open asset mismatch/inactive CMT for this asset, do not show the message and do no create a new CMT.
    if (this.showCMTChanges == "1" && (asset.value.cmtNumber == null || (asset.value.cmtNumber != null && asset.value.cmtNumber != undefined && asset.value.cmtStatus == "Closed"))) {

      // if asset has future demand and no production demand, on selecting active status show message, create CMT:
      if (!asset.value.hasProductionDemand && asset.value.hasFutureDemand && event.value == "active") {
        this.dialog.alert(this.translate.get('Error'), this.translate.get('This asset does not have production demand parts. Asset Mismatch CMT will be created.'));
      }
      // if asset has service demand and no production and future demands, on selecting active or future status show message, create CMT:
      else if (!asset.value.hasProductionDemand && !asset.value.hasFutureDemand && asset.value.hasServiceDemand && event.value == "active") {
        this.dialog.alert(this.translate.get('Error'), this.translate.get('This asset does not have production demand parts. Asset Mismatch CMT will be created.'));
      }
      else if (!asset.value.hasProductionDemand && !asset.value.hasFutureDemand && asset.value.hasServiceDemand && event.value == "future") {
        this.dialog.alert(this.translate.get('Error'), this.translate.get('This asset does not have future demand parts. Asset Mismatch CMT will be created.'));
      }
      else if ((asset.value.hasProductionDemand || asset.value.hasFutureDemand || asset.value.hasServiceDemand) && event.value == "notactive") {
        this.dialog.alert(this.translate.get('Error'), this.translate.get('This asset has parts with production/future/service demand. Asset Disposal Request will be created.'));
      }
    }
  }

  onChangeSubcategory(event: TslSelectChange): void {
    const subCategoryId = parseInt(event.value || 0, 0);
    this.parametersData = [];
    this.service.getParameterType(this.currentTool.supplierCode, subCategoryId)
      .subscribe(res => {
        this.parametersData = res.map(p => {
          const index = this.currentTool.data.parameter.findIndex(d => d.typeId === p.id);
          return {
            typeId: p.id,
            typeName: p.type,
            value: index !== -1 ? this.currentTool.data.parameter[index].value : '',
            supplierCode: p.supplierCode,
            status: p.status,
            isDefault: p.isDefault
          };
        });
        if (this.parametersData && this.parametersData.length > 0) {
          this.parametersData = this.parametersData.filter(x => x.status !== "Submitted" && x.status !== "Rejected");
        }
      });

    // get default deliverables
    if (subCategoryId > 0) {
      this.defaultDeliverables = [];
      this.service.getDefaultDeliverables().subscribe(defaults => {
        if (defaults && defaults.length > 0) {
          this.defaultDeliverables = defaults.filter(x => x.toolSubcategoryId == subCategoryId);
          this.defaultDeliverables.forEach(x => {
            switch (x.eventType) {
              case 'Design':
                x.eventType = 0;
                break;
              case 'Build':
                x.eventType = 1;
                break;
              case 'Commission':
                x.eventType = 2;
                break;
              case 'Maintenance':
                x.eventType = 3;
                break;
            }
          });

          this.defaultDeliverables = this.defaultDeliverables.map(x => {
            return {
              eventType: x.eventType,
              deliverableTypeId: x.id,
              deliverable: x.deliverable,
              isApproved: false,
              status: ToolDeliverableStatus.NotSubmitted,
              isDefault: x.isDefault
            }
          });

          this.onLoadDeliverables();
        }
      });
    }
  }

  onTagFileAdd(files: File[]): void {
    files.forEach(file => {
      if (file.size > this.fileSizeLimitMB) {
        this.dialog.alert(this.translate.get('Alert'), this.translate.get('File size can not be more than 10MB!'));
        return;
      }

      this.service.postToolAttachment(this.currentTool.supplierCode, file)
        .subscribe(res => {
          this.currentTool.data.tagAttachment.push(res);
        });
    });
  }

  onTagFileDelete(attach: Attachment, index: number): void {
    this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.tagFileDrop.reset(true);
          this.currentTool.data.tagAttachment.splice(index, 1);
        }
      });
  }

  onAssetFileAdd(files: File[]): void {
    files.forEach(file => {
      if (file.size > this.fileSizeLimitMB) {
        this.dialog.alert(this.translate.get('Alert'), this.translate.get('File size can not be more than 10MB!'));
        return;
      }

      this.service.postToolAttachment(this.currentTool.supplierCode, file)
        .subscribe(res => {
          this.currentTool.data.assetAttachment.push(res);
        });
    });
  }

  onAssetFileDelete(attach: Attachment, index: number): void {
    this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.assetFileDrop.reset(true);
          this.currentTool.data.assetAttachment.splice(index, 1);
        }
      });
  }

  onAttachmentDelete(deliverable: ToolDeliverable, attachId: number): void {
    this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          deliverable.attachments.splice(attachId, 1);
        }
      })
  }

  onDownloadFile(attach: Attachment): void {
    this.service.downloadCertificationAttachment(this.currentTool.supplierCode, attach.key)
      .subscribe(file => {
        FileSaver.saveAs(file, attach.name);
      });
  }

  assetAutoDisplay(asset: Asset): string {
    return asset !== undefined ? `${asset.assetCode} - ${asset.assetDescription}` : '';
  }

  locationAutoDisplay(item: AddressLocation): string {
    return `${item.name} ${item.address1} ${item.city} ${item.state} ${item.zipcode} ${item.country}`;
  }

  onEditParam(param: ToolParameter): void {
    this.openParameterDialog(param);
  }

  onAddParam(): void {
    this.openAddParameterDialog();
  }

  onAddEvent(eventType: number): void {
    const model = new ToolDeliverable();
    model.eventType = eventType;
    this.openDeliverableDialog(eventType, model);
  }

  onEditDeliverable(eventType: number, model: ToolDeliverable): void {
    this.openDeliverableDialog(eventType, model);
  }

  onRemoveDeliverable(model: ToolDeliverable): void {
    const index = this.deliverablesData.indexOf(model);
    if (index !== -1) {
      this.dialog.confirm(this.deleteTitle, this.deleteConfirmation)
        .afterClosed()
        .subscribe(confirmed => {
          if (confirmed) {
            this.deliverablesData.splice(index, 1);
            this.refreshDeliverables();
          }
        });
    }
  }

  getFileType(file: Attachment): string {
    const nameArray = file.name.toLowerCase().split(".");
    return nameArray[nameArray.length - 1];
  }

  isDeliverableHeader(index, item): boolean {
    return item.isGroup;
  }

  isHeader(index, item): boolean {
    return item.isGroup;
  }

  private onAddSelectedAsset(asset: Asset): void {
    this.assets.setErrors(null);
    let toolAsset = new ToolAsset();
    toolAsset.assetCode = asset.assetCode;
    toolAsset.assetDescription = asset.assetDescription;
    toolAsset.assetId = asset.assetId;

    if (this.showCMTChanges == "1") {
      toolAsset.cmtNumber = asset.cmtNumber;
      toolAsset.createCMT = asset.createCMT;
      toolAsset.cmtStatus = asset.cmtStatus;
      toolAsset.partNumbers = asset.partNumbers;
    }

    this.assetSelected.push(toolAsset); // add this asset to the tool.

    if (this.showCMTChanges == "1") {
      this.assets.push(this.fb.group({
        assetCode: [toolAsset.assetCode, Validators.required],
        assetId: [toolAsset.assetId, Validators.required],
        assetDescription: [toolAsset.assetDescription, Validators.required],
        assetStatus: ['', Validators.required],
        hasProductionDemand: [false, []],
        hasFutureDemand: [false, []],
        hasServiceDemand: [false, []],
        hasNoDemand: [false, []],
        showActiveStatus: [false, []],
        showFutureStatus: [false, []],
        showServiceStatus: [false, []],
        showNoDemandStatus: [false, []],
        partNumbers: [toolAsset.partNumbers, Validators.required],
        demandTypes: [toolAsset.demandTypes, Validators.required],
        createCMT: [toolAsset.createCMT, []],
        cmtNumber: [toolAsset.cmtNumber, []],
        cmtStatus: [toolAsset.cmtStatus, []]
      }));
    }
    else {
      this.assets.push(this.fb.group({
        assetCode: [toolAsset.assetCode, Validators.required],
        assetId: [toolAsset.assetId, Validators.required],
        assetDescription: [toolAsset.assetDescription, Validators.required],
        assetStatus: ['', Validators.required]
      }));
    }

    this.assetList.splice(this.assetList.findIndex(x => x.assetId == asset.assetId), 1);

    this.assets.markAsDirty();

    if (asset.partNumbers) {
      let assetData = "";
      if (this.currentTool.data && this.currentTool.data.assets && this.currentTool.data.assets.length > 0) {
        assetData = this.currentTool.data.assets.map(x => x.assetCode).join(", ");
      }

      if (assetData == "") {
        assetData += toolAsset.assetCode;
      }
      else {
        assetData += ", " + toolAsset.assetCode;
      }

      let partNumbers = asset.partNumbers.split(',');
      if (partNumbers && partNumbers.length > 0) {
        if (!this.associatedPartsData) {
          this.associatedPartsData = [];
        }
        this.service.getPartSupplierDetails(this.currentTool.supplierCode, partNumbers).subscribe((partsuppliers: PartSupplierDetails[]) => {
          if (partsuppliers) {

            partsuppliers.forEach(x => {
              if (!this.associatedPartsData.some(y => y.partNumber == x.partNumber && y.isGroup)) {
                this.associatedPartsData.push({ partNumber: x.partNumber, partDescription: x.partDescription, assets: assetData, caseNumber: '', demandType: x.demandType, isGroup: true });
                this.associatedPartsData.push({ partNumber: x.partNumber, partDescription: x.partDescription, caseNumber: '', isGroup: false, demandType: x.demandType });
                let associatedPart = new AssociatedPart();
                associatedPart.partId = x.partID;
                associatedPart.partNumber = x.partNumber;
                associatedPart.partDescription = x.partDescription;
                if (!this.currentTool.data.associatedParts) {
                  this.currentTool.data.associatedParts = [];
                }
                this.currentTool.data.associatedParts.push(associatedPart);
              }
            });
            this.refreshAssociatedParts();
            if (this.showCMTChanges == "1") {
              // every time a Tesla asset is added, update the asset data (partnumbers, demand types, dropdown flags):
              this.setStatusForTeslaAsset();
            }

          }
        });
      }
    }
    else {
      if (this.associatedPartsData && this.associatedPartsData.length > 0) {
        let assetData = "";
        if (this.currentTool.data && this.currentTool.data.assets && this.currentTool.data.assets.length > 0) {
          assetData = this.currentTool.data.assets.map(x => x.assetCode).join(", ");
        }

        if (assetData == "") {
          assetData += toolAsset.assetCode;
        }
        else {
          assetData += ", " + toolAsset.assetCode;
        }

        this.associatedPartsData.forEach(x=>{
          if(x.isGroup == true){
            x.assets = assetData;
          }
        })

      }
      if (this.showCMTChanges == "1") {
        // every time a Tesla asset is added, update the asset data (partnumbers, demand types, dropdown flags):
        this.setStatusForTeslaAsset();
      }
    }
  }

  toolStatusChanged() {
    this.toolGroup.valueChanges.
      subscribe(
        changedValue => {

        });
  }

  openPopup(): boolean {
    let openForStatusChange: boolean = false;
    let openForAssociatesChange: boolean = false;
    if (this.currentTool.data.status) {
      openForStatusChange = this.status.value !== this.currentTool.data.status;
    } else {
      openForStatusChange = true;
    }
    if (this.currentTool.data.associatedParts.length !== (this.currentAssociatedParts.length / 2)) {
      openForAssociatesChange = true;
    } else {
      for (let i = 0; i < this.currentTool.data.associatedParts.length; i++) {
        if (this.currentTool.data.associatedParts[i].partNumber !== this.currentAssociatedParts[i * 2].partNumber) {
          openForAssociatesChange = true;
        }
      }
    }
    return openForStatusChange || openForAssociatesChange;
  }

  private openMismatchCommentBox(): void {
    if (!this.openPopup()) {
      this.onSave();
    }
    else if (this.isTeslaAsset.value) {
      this.onSave();
    } else {
      if (this.associatedPartsHasMismatch()) {
        const dialogRef = this.matDialog.open(OverrideCommentDialog,
          {
            width: '700px',
            height: '360px',
            data: {
              toolData: this.currentTool,
              status: this.toolGroup.value.status,
            }
          });

        dialogRef.afterClosed()
          .subscribe(res => {
            if (res === undefined) {
              return;
            }
            this.currentTool.data.statusMismatchOverrideComment = res.toolData.data.statusMismatchOverrideComment;
            this.onSave();
            //this.refreshDeliverables();
          });
      } else {
        this.onSave();
      }
    }
  }

  private openDeliverableDialog(eventType: number, editModel: ToolDeliverable): void {
    const dialogRef = this.matDialog.open(DeliverableFormDialogComponent,
      {
        width: '600px',
        height: '450px',
        data: {
          eventType: eventType,
          subCategoryId: this.toolGroup.value.toolSubcategory,
          supplierCode: this.currentTool.supplierCode,
          model: editModel
        }
      });

    dialogRef.afterClosed()
      .subscribe(res => {
        if (res === undefined) {
          return;
        }

        // update changes to unify source
        const index = this.deliverablesData.indexOf(res.model);
        if (index !== -1) {
          this.deliverablesData[index] = res.model;
        } else {
          const groupInd = this.deliverablesData.findIndex(d => d.eventType === res.eventType && d.isGroup);
          const numPerGroup = this.deliverablesData.filter(d => d.eventType === res.eventType).length;
          if (groupInd !== -1) {
            this.deliverablesData.splice(groupInd + numPerGroup, 0, res.model);
          }
        }

        this.refreshDeliverables();
      });
  }

  private openReOrderDialog(): void {
    const dialogRef = this.matDialog.open(ReorderDeliverablesDialog,
      {
        width: '700px',
        height: '460px',
        data: {
          deliverablesList: JSON.parse(JSON.stringify(this.deliverablesData)),
        }
      });

    dialogRef.afterClosed()
      .subscribe(res => {
        if (res === undefined) {
          return;
        }

        this.deliverablesData = res.toolData;
        this.refreshDeliverables();
      });
  }

  private openSupplierComment(...submissionData: any[]): void {
    const dialogRef = this.matDialog.open(CommentDataDialog,
      {
        width: '600px',
        height: '200px',
        data: {
          submissionState: true,
          name: submissionData[0],
          date: submissionData[1],
          comment: submissionData[2],
        }
      });

    dialogRef.afterClosed().subscribe();
  }

  private openReviewComment(...reviewData: any[]): void {
    const dialogRef = this.matDialog.open(CommentDataDialog,
      {
        width: '600px',
        height: '200px',
        data: {
          submissionState: false,
          name: reviewData[0],
          date: reviewData[1],
          comment: reviewData[2],
        }
      });

    dialogRef.afterClosed().subscribe();
  }

  private openParameterDialog(model: ToolParameter): void {
    const dialogRef = this.matDialog.open(ParameterFormDialogComponent,
      {
        width: '400px',
        data: {
          supplierCode: this.currentTool.supplierCode,
          subCategoryId: this.toolGroup.value.toolSubcategory,
          param: model
        }
      });

    dialogRef.afterClosed()
      .subscribe(param => {
        if (param === undefined) {
          return;
        }

        const paramIndex = this.parametersData.findIndex(p => p.typeId === param.typeId);
        if (paramIndex !== -1) {
          this.parametersData[paramIndex].value = param.value;
        }
      });
  }

  openAddParameterDialog(): void {
    const dialogRef = this.matDialog.open(ParameterTypeDialogComponent,
      {
        width: '500px',
        data: {
          supplierCode: this.currentTool.supplierCode,
          categoryId: this.toolGroup.value.toolCategory,
          subCategoryId: this.toolGroup.value.toolSubcategory,
          role: this.role
        }
      });

    dialogRef.afterClosed()
      .subscribe((param: ToolParameterType | any) => {
        if (!param) {
          return;
        }

        if (param.toolSubcategoryId === this.toolGroup.value.toolSubcategory) {
          this.parametersData.push({
            typeId: param.id,
            typeName: param.type,
            value: param.value
          });
        }
      });
  }

  private loadResources(): void {
    this.associatedPartsData = [];
    this.store.dispatch(new GetToolCategories());
    this.store.dispatch(new GetToolSubCategories(this.currentTool.supplierCode, false));
    combineLatest(
      [this.store.select(SelectToolCategories),
      this.store.select(SelectToolSubCategories)])
      .subscribe(([categories, subCategories]) => {
        if (categories && subCategories) {
          this.filterCategories = categories.sort((a, b) => a.category.trim().toLowerCase().localeCompare(b.category.trim().toLowerCase(), undefined, { sensitivity: 'base' }));;
          this.allSubcategories = subCategories[this.currentTool.supplierCode].sort((a, b) => a.subcategory.trim().toLowerCase().localeCompare(b.subcategory.trim().toLowerCase(), undefined, { sensitivity: 'base' }));
        }
      });

    this.service.getAssets(this.currentTool.supplierCode, false)
      .subscribe(res => {
        this.allAssetList = res;
        this.setupCmtStatusAfterGettingAssetList(this.allAssetList);

        this.assetList = this.allAssetList.filter(x => x.isAvailable === true);

        this.setLocations();
        let assetData = "";
        if (this.currentTool.data && this.currentTool.data.assets) {
          assetData = this.currentTool.data.assets.map(x => x.assetCode).join(", ");
        }

        // get associated process flow parts:
        if (this.currentTool.id) {
          this.service.getProcessFlowByToolId(this.currentTool.supplierCode, this.currentTool.id).subscribe((res: ToolProcessFlow[]) => {
            this.processFlows = res;
            this.processFlows.forEach(x => {
              this.associatedPartsData.push({ partNumber: x.partNumber, partDescription: x.partDescription, assets: assetData, demandType: x.demandType, caseNumber: x.caseNumber, isGroup: true });
              if (x.processSteps) {
                x.processSteps.forEach(ps => {
                  const partData: AssociatedPart = JSON.parse(JSON.stringify(x));
                  partData.processStepName = ps.processStepName;
                  partData.associatedRequirements = ps.processStepDetails.processStepElement.requirements.dependentRequirements.map(r => r.title);
                  partData.mistakePreventions = ps.processStepDetails.processStepElement.mistakePrevention.map(m => m.description);
                  this.associatedPartsData.push(partData);
                });
              }

            });
            this.refreshAssociatedParts();
            if (this.showCMTChanges == "1") {
              this.setStatusForTeslaAsset();
            }
          });
        }

        let partNumbers = [];
        if (this.currentTool.data.associatedParts) {
          partNumbers = this.currentTool.data.associatedParts.map(x => x.partNumber);
        }

        if (this.currentTool.data.assets) {
          let parts = this.allAssetList.filter(x => this.currentTool.data.assets.some(y => y.assetId == x.assetId)).map(asset => asset.partNumbers);
          parts.forEach(x => {
            partNumbers = partNumbers.concat(...x.split(','));
          });

          // Remove duplicates
          partNumbers = partNumbers.filter((a, b) => partNumbers.indexOf(a) === b);
        }

        partNumbers = partNumbers.filter(x => x && x != '');

        if (!this.associatedPartsData) {
          this.associatedPartsData = [];
        }

        this.currentTool.data.associatedParts = [];

        // get associated parts data from part supplier details:
        if (partNumbers.length > 0) {
          this.service.getPartSupplierDetails(this.currentTool.supplierCode, partNumbers).subscribe((partsuppliers: PartSupplierDetails[]) => {
            if (partsuppliers) {
              partsuppliers.forEach(x => {
                if (!this.associatedPartsData.some(y => y.partNumber == x.partNumber && y.isGroup)) {
                  this.associatedPartsData.push({ partNumber: x.partNumber, partDescription: x.partDescription, assets: assetData, caseNumber: '', demandType: x.demandType, isGroup: true });
                  this.associatedPartsData.push({ partNumber: x.partNumber, partDescription: x.partDescription, caseNumber: '', isGroup: false, demandType: x.demandType });
                  let associatedPart = new AssociatedPart();
                  associatedPart.partId = x.partID;
                  associatedPart.partNumber = x.partNumber;
                  associatedPart.partDescription = x.partDescription;
                  if (!this.currentTool.data.associatedParts) {
                    this.currentTool.data.associatedParts = [];
                  }
                  this.currentTool.data.associatedParts.push(associatedPart);
                }
              });
              this.refreshAssociatedParts();

              if (this.showCMTChanges == "1") {
                this.setStatusForTeslaAsset();
                if (this.currentTool.data && this.currentTool.data.assets && this.currentTool.data.assets.length > 0) {
                  this.currentTool.data.assets.forEach(item => {
                    if (item.cmtNumber && item.cmtNumber.includes('SPT-AC-') && (item.cmtStatus == 'Pending Tool Create By Supplier' || item.cmtStatus == 'Closed')) {
                      var currentAssetInfo = this.allAssetList.find(x => x.assetCode == item.assetCode);
                      if (currentAssetInfo != null && currentAssetInfo != undefined) {
                        item.partNumbers = currentAssetInfo.partNumbers;
                        let demandTypes = [];
                        this.associatedPartsData.forEach(y => {
                          if (partNumbers && partNumbers.includes(y.partNumber) && y.isGroup) {
                            demandTypes.push(y.demandType);
                          }
                          if (demandTypes && demandTypes.length > 0) {
                            item.demandTypes = demandTypes.join(',');
                          }
                        });
                      }
                    }
                  });
                }
              }
              this.assetSelected = this.currentTool.data.assets || [];
              this.savedAssets = this.currentTool.data.assets != null ? this.currentTool.data.assets.length : 0;
              if (this.isTeslaAsset.value == true) {
                this.toolGroup.setControl('assets', this.fb.array(this.getAssetGroup(), CustomValidator.arrayRequiredValidator));
              }
              else {
                this.toolGroup.setControl('assets', this.fb.array(this.getAssetGroup()));
              }
            }
          });
        }
      });

    this.translate.getAsync('Delete Item').subscribe(t => this.deleteTitle = t);
    this.translate.getAsync('Are you sure you want to delete this item?').subscribe(t => this.deleteConfirmation = t);
    this.translate.getAsync('Duplicate Asset').subscribe(t => this.duplicateAssetTitle = t);
    this.translate.getAsync('Duplicate Assets cannot be deleted').subscribe(t => this.duplicateAssetMessage = t);
  }

  private openAssociatedPartsDialog(): void {
    const dialogRef = this.dialog.open(AssociatedPartsFormDialogComponent,
      {
        width: '500px',
        data: {
          associatedParts: this.associatedPartsData,
          supplierCode: this.currentTool.supplierCode
        }
      });

    dialogRef.afterClosed()
      .subscribe(res => {
        if (res === undefined) {
          return;
        }

        // update changes to unify source
        //everything in this if needs to be in a for loop
        for (var i = 0; i < res.length; i++) {
          let temp = res[i];
          const index = this.associatedPartsData.findIndex(x => x.partNumber == temp.partNumber);
          if (index === -1) {
            this.service.getPartSupplierDetails(this.currentTool.supplierCode, [temp.partNumber]).subscribe(partSupplierData => {
              if (partSupplierData && partSupplierData.length == 1) {
                let assetData: string = '';

                if (this.currentTool.data.assets && this.currentTool.data.assets.length > 0) {
                  assetData = this.currentTool.data.assets.map(x => x.assetCode).join(", ");
                }
                this.associatedPartsData.push({ partNumber: temp.partNumber, partDescription: temp.description, assets: assetData, caseNumber: '', demandType: partSupplierData[0].demandType, isGroup: true, isProcessFlowPart: false });
                this.associatedPartsData.push({ partNumber: temp.partNumber, partDescription: temp.description, caseNumber: '', isGroup: false, demandType: partSupplierData[0].demandType, isProcessFlowPart: false });
                this.refreshAssociatedParts();
                let associatedPart = new AssociatedPart();
                associatedPart.partId = temp.partID;
                associatedPart.partNumber = temp.partNumber;
                associatedPart.partDescription = temp.description;
                associatedPart.isProcessFlowPart = false; //manually added parts.

                if (!this.currentTool.data.associatedParts) {
                  this.currentTool.data.associatedParts = [];
                }
                this.currentTool.data.associatedParts.push(associatedPart);

                if (this.showCMTChanges == "1") {
                  // every time a part is added, update the asset data (partnumbers, demand types, dropdown flags) :
                  this.setStatusForTeslaAsset();
                }
              }
            });

          }
        };
      });
    this.associatedPartsHasMismatch();

  }

  associatedPartsHasMismatch(): boolean {
    if (!this.toolGroup.value.status) {
      return false;
    }
    for (var parts of this.associatedPartsData) {
      if ((this.toolGroup.value.status == "future" && (parts.demandType != "No Demand" && (parts.demandType != "Future Production Demand" || parts.demandType != "Future Service Demand")))) {
        return true;
      }
      if (this.toolGroup.value.status == "service" && parts.demandType != "Service Demand") {
        return true;
      }
      if ((this.toolGroup.value.status == "notactive" && (parts.demandType != "No Demand" && (parts.demandType != "Future Production Demand" || parts.demandType != "Future Service Demand")))) {
        return true;
      }
      if (this.toolGroup.value.status == "active" && parts.demandType == "Production Demand") {
        return false;
      }
    }
    if (this.toolGroup.value.status == "active") {
      return true;
    }
    return false;
  }

  refreshAssociatedParts(): void {
    this.associatedPartsData = JSON.parse(JSON.stringify(this.associatedPartsData));
  }

  private filterAsset(value: string): Asset[] {
    const filterValue = value.toLowerCase();
    if (this.assetList) {
      return this.assetList.filter(
        option => {
          return option.assetCode
            .toLowerCase()
            .includes(filterValue);
        });
    }
  }

  private filterLocation(value: string): AddressLocation[] {
    const filterValue = value.toLowerCase();
    if (this.locations) {
      return this.locations.filter(
        option => {
          const fullAddress = this.locationAutoDisplay(option);
          return fullAddress
            .toLowerCase()
            .includes(filterValue);
        });
    }
  }

  /**
   * Create all option forms.
   */
  private createForms(): void {
    this.toolGroup = this.fb.group({
      toolCategory: ['', Validators.required],
      toolSubcategory: ['', Validators.required],
      name: ['', [Validators.required, CustomValidator.noWhitespaceValidator, Validators.pattern("^([0-9a-zA-Z\-_:.\'' ])+$")]],
      status: [{ value: '', disabled: true }],
      isTeslaAsset: [''],
      assetTagNumber: [{ value: '', disabled: true }, Validators.required],
      identifier: ['', [Validators.pattern("^([0-9a-zA-Z\-_:.\'' ])+$")]],
      isExpendable: [''],
      expirationDate: [''],
      numberOfParts: [''],
      quantity: [''],
      location: [''],
      description: [''],
      assets: this.fb.array([])
    }, { validators: expendableFieldValidator });

    if (this.assets.valid) {
      this.toolGroup.get('assetTagNumber').clearValidators();
      this.toolGroup.get('assetTagNumber').updateValueAndValidity();
    }

    this.toolGroup.valueChanges.
      subscribe(checkValue => {
        const assetTagNum = this.toolGroup.get('assetTagNumber');
        if (this.assets.valid) {
          assetTagNum.clearValidators();
        }
        assetTagNum.updateValueAndValidity({ emitEvent: false });
      });

    this.assetsFiltered$ = this.assetTagNumber.valueChanges
      .pipe(
        debounceTime(300),
        startWith(' '),
        map(value => this.filterAsset(value))
      );

    this.locationsFiltered$ = this.location.valueChanges
      .pipe(
        debounceTime(300),
        startWith(' '),
        map(value => this.filterLocation(value))
      );

    this.locationsFiltered$.subscribe(x => {
      x.forEach(element => this.locationID.push(element.id));
    });

  }

  private getAssetGroup(): FormGroup[] {
    if (this.showCMTChanges == "1") {
      return this.assetSelected.map(y => {
        let x: Asset = y as Asset;
        return this.fb.group({
          assetCode: [x.assetCode, Validators.required],
          assetId: [x.assetId, Validators.required],
          assetDescription: [x.assetDescription, Validators.required],
          assetStatus: [x.assetStatus, Validators.required],
          hasProductionDemand: [x.hasProductionDemand, []],
          hasFutureDemand: [x.hasFutureDemand, []],
          hasServiceDemand: [x.hasServiceDemand, []],
          hasNoDemand: [x.hasNoDemand, []],
          showActiveStatus: [x.showActiveStatus, []],
          showFutureStatus: [x.showFutureStatus, []],
          showServiceStatus: [x.showServiceStatus, []],
          showNoDemandStatus: [x.showNoDemandStatus, []],
          partNumbers: [x.partNumbers, Validators.required],
          demandTypes: [x.demandTypes, Validators.required],
          createCMT: [x.createCMT, []],
          cmtNumber: [x.cmtNumber, []],
          cmtStatus: [x.cmtStatus, []],
        })
      });
    }
    else {
      return this.assetSelected.map(x => {
        return this.fb.group({
          assetCode: [x.assetCode, Validators.required],
          assetId: [x.assetId, Validators.required],
          assetDescription: [x.assetDescription, Validators.required],
          assetStatus: [x.assetStatus, Validators.required]
        })
      });
    }
  }

  /**
   * Set data for current tool.
   */
  private setCurrentTool(): void {
    if (this.assets.value.length > 0) {
      this.toolGroup.get('assetTagNumber').clearValidators();
      this.toolGroup.get('assetTagNumber').updateValueAndValidity();
    } else {
      this.toolGroup.get('assetTagNumber').setValidators([Validators.required]);
      this.toolGroup.get('assetTagNumber').updateValueAndValidity();
    }
    this.toolSelected = JSON.parse(JSON.stringify(this.currentTool));
    this.assetSelected = this.currentTool.data.assets || [];
    this.savedAssets = this.currentTool.data.assets != null ? this.currentTool.data.assets.length : 0;

    this.toolGroup.setControl('assets', this.fb.array(this.getAssetGroup()));

    this.toolGroup.patchValue({
      isTeslaAsset: this.currentTool.data.isTeslaAsset != null ? this.currentTool.data.isTeslaAsset : '',
      toolCategory: this.currentTool.toolCategoryId || '',
      identifier: this.currentTool.data.identifier || '',
      isExpendable: this.currentTool.data.isExpendable != null ? this.currentTool.data.isExpendable : '',
      numberOfParts: this.currentTool.data.numberOfParts,
      expirationDate: this.currentTool.data.expirationDate,
      quantity: this.currentTool.data.quantity || '',
      name: this.currentTool.data.name || '',
      status: this.currentTool.data.status || '',
      description: this.currentTool.data.description || '',
      assets: this.assetSelected
    });

    if (this.currentTool.id && this.currentTool.data.isTeslaAsset) {
      this.isTeslaAsset.disable();
    }

    this.isExpendable.valueChanges.subscribe((expendable: boolean) => {
      this.toolGroup.patchValue({
        expirationDate: null,
        numberOfParts: null
      });
    });

    this.changeToolAssetValue();
    this.del = JSON.parse(JSON.stringify(this.currentTool.data.deliverable));

    if (this.currentTool.toolSubcategoryId && this.currentTool.toolSubcategoryId > 0) {
      this.onLoadDeliverables();
    }

    // trigger events
    this.onChangeCategory({ source: this.categoryRef, value: this.currentTool.toolCategoryId });
    this.onIsTeslaAsset();
  }

  /**
  * Set Tool Status based on asset's status.
  */
  changeToolAssetValue() {
    this.assets.valueChanges.subscribe((toolAssets: ToolAsset[]) => {
      if (toolAssets && toolAssets.length > 0) {
        if (toolAssets.some(x => x.assetStatus && x.assetStatus.toLocaleLowerCase() == "active")) {
          this.currentTool.data.status = "active";
        }
        else if (toolAssets.some(x => x.assetStatus && x.assetStatus.toLocaleLowerCase() == "future")) {
          this.currentTool.data.status = "future";
        }
        else if (toolAssets.some(x => x.assetStatus && x.assetStatus.toLocaleLowerCase() == "service")) {
          this.currentTool.data.status = "service";
        }
        else if (toolAssets.every(x => x.assetStatus && x.assetStatus.toLocaleLowerCase() == "notactive")) {
          this.currentTool.data.status = "notactive";
        }
        else {
          this.currentTool.data.status = this.currentTool.data.status || '';
        }

        if (!this.currentTool.id && this.currentTool.data && !this.currentTool.data.locationId) {
          // Check if all assets has same location - select that location
          let assetDataList = this.allAssetList.filter(x => toolAssets.some(y => y.assetId == x.assetId));
          const locationIds = assetDataList.map(x => { if (x.residingLocationId) { return x.residingLocationId; } })
          const uniqueLocationIds = new Set(locationIds);
          if (uniqueLocationIds.size == 1) {
            const locationId = parseInt(`${ToolLocationOrigin.manufacturingLocation}${locationIds[0]}`);
            const locations = this.locations.filter(x => x.id === locationId);
            this.location.setValue(locations && locations.length > 0 ? this.locationAutoDisplay(locations[0]) : '');
            this.currentTool.data.locationId = locationId;
          }

        }

        this.toolGroup.patchValue({
          status: this.currentTool.data.status || '',
        });
      }
    });
  }

  private setLocation(): void {
    const locations = this.locations.filter(x => x.id === this.currentTool.data.locationId);
    this.location.setValue(locations.length > 0 ? this.locationAutoDisplay(locations[0]) : '');
  }

  private onLoadDeliverables(): void {
    // edit case remove deafult deliverables already present
    if (this.currentTool.data.deliverable && this.currentTool.data.deliverable.length > 0 && this.defaultDeliverables && this.defaultDeliverables.length > 0) {
      this.currentTool.data.deliverable.forEach(def => {
        this.defaultDeliverables = this.defaultDeliverables.filter(x => x.deliverableTypeId != def.deliverableTypeId)
      });
    }

    // create single datasource for deliverables
    this.deliverablesData = [];

    // Desing Deliverables
    // Rather then use concat, use some sort of union method for arrays
    // Remove the if statement to check of the tool is new and use union
    // Design
    this.deliverablesData.push({ isGroup: true, eventType: 0, name: 'Design' });
    this.deliverablesData = this.deliverablesData.concat([...new Set([...this.currentTool.data.deliverable.filter(d => d.eventType === 0), ...this.defaultDeliverables.filter(d => d.eventType === 0)])]);

    // Build
    this.deliverablesData.push({ isGroup: true, eventType: 1, name: 'Build' });
    this.deliverablesData = this.deliverablesData.concat([...new Set([...this.currentTool.data.deliverable.filter(d => d.eventType === 1), ...this.defaultDeliverables.filter(d => d.eventType === 1)])]);

    // Commission
    this.deliverablesData.push({ isGroup: true, eventType: 2, name: 'Commission' });
    this.deliverablesData = this.deliverablesData.concat([...new Set([...this.currentTool.data.deliverable.filter(d => d.eventType === 2), ...this.defaultDeliverables.filter(d => d.eventType === 2)])]);

    // Maintenance
    this.deliverablesData.push({ isGroup: true, eventType: 3, name: 'Maintenance' });
    this.deliverablesData = this.deliverablesData.concat([...new Set([...this.currentTool.data.deliverable.filter(d => d.eventType === 3), ...this.defaultDeliverables.filter(d => d.eventType === 3)])]);
  }

  private onSaveDeliverables(): void {
    this.currentTool.data.deliverable = this.deliverablesData.filter(d => d.isGroup === undefined) || [];
    this.currentTool.data.parameter = this.parametersData;
  }

  private setLocations(): void {
    this.store.dispatch(new GetSupplierLocationsByCode(this.currentTool.supplierCode, true));
    combineLatest([this.peopleService.getExternalPartners(this.currentTool.supplierCode),
    this.store.select(SelectSupplierLocations)])
      .subscribe(([externalPartners, supplierLocations]) => {
        if (externalPartners && supplierLocations) {
          this.locations = this.locations.concat(
            externalPartners.map(r => {
              return {
                id: parseInt(`${ToolLocationOrigin.externalPartner}${r.id}`, 0),
                name: r.companyName || '',
                address1: r.address1 || '',
                city: r.city || '',
                state: r.state || '',
                country: r.country !== undefined ? r.country.abbreviation : '',
                zipcode: r.zipCode || '',
              };
            })
          );

          this.locations = this.locations.concat(
            supplierLocations.map(r => {
              return {
                id: parseInt(`${ToolLocationOrigin.manufacturingLocation}${r.id}`, 0),
                name: '[Site]',
                address1: r.properties.address || '',
                city: r.properties.city || '',
                state: r.properties.state || '',
                country: r.properties.country || '',
                zipcode: r.properties.zip || '',
              };
            })
          );
        }
        this.setLocation();
      });
  }

  private refreshDeliverables(): void {
    const stringData = JSON.stringify(this.deliverablesData);
    this.deliverablesData = JSON.parse(stringData);
  }

  // SAVE TOOL DATA METHODS:
  verifyAssetMismatchOnSave() {
    this.assets.value.forEach(element => {
      // if asset has future demand and no production demand, on selecting active status show message, create CMT:
      if ((element.cmtNumber == null || (element.cmtNumber != null && element.cmtStatus == "Closed")) && !element.hasProductionDemand && element.hasFutureDemand && element.assetStatus == "active") {
        element.createCMT = true;
        this.errortable.push(new AssetMismatch(element.assetCode, element.assetStatus, element.partNumbers, element.demandTypes, ''));
      }
      // if asset has service demand and no production and future demands, on selecting active and future status show message, create CMT:
      else if ((element.cmtNumber == null || (element.cmtNumber != null && element.cmtStatus == "Closed")) && !element.hasProductionDemand && !element.hasFutureDemand && element.assetStatus == "active") {
        element.createCMT = true;
        this.errortable.push(new AssetMismatch(element.assetCode, element.assetStatus, element.partNumbers, element.demandTypes, ''));
      }
      // if asset has production/future/service/no demand, on selecting not active asset status, show message, create CMT:
      else if ((element.cmtNumber == null || (element.cmtNumber != null && element.cmtStatus == "Closed")) && (element.hasProductionDemand || element.hasFutureDemand || element.hasServiceDemand || element.hasNoDemand) && element.assetStatus == "notactive") {
        element.createCMT = true;
        this.errortable.push(new AssetMismatch(element.assetCode, element.assetStatus, element.partNumbers, element.demandTypes, ''));
      }
      else if (!element.hasProductionDemand && !element.hasFutureDemand && !element.hasServiceDemand && (element.assetStatus != null && element.assetStatus != "notactive" && element.assetStatus != "future")) {
        this.dialog.alert(this.translate.get('Error'), this.translate.get('Cannot save asset in active/future/service status if no demand exists.'));
        this.hasAssetStatusError = true;
        return;
      }
    });

    if (this.errortable.length > 0) {
      const AssetMismatchComponentDialogRef = this.dialog.open(AssetMismatchComponent, {
        disableClose: true,
        width: '500px',
        data: this.errortable
      });
      AssetMismatchComponentDialogRef.afterClosed().subscribe(data => {
        this.errortable = [];
        if (data != null && data.data == true) {
          this.saveData();
        }
      });
    }
    else if (!this.hasAssetStatusError) {
      this.saveData();
    }
  }

  checkForAnyOpenCases() {
    let hasOpenCase = false;
    this.assets.value.forEach(element => {
      if (element && element.cmtNumber != null && element.cmtStatus != null) {
        // check if the case open is for confirmation cmt (with Pending Supplier Action and Pending Tesla Action) or Inactive CMT
        if ((element.cmtNumber.includes('SPT-AC-')
          && (element.cmtStatus == 'Pending Tesla Action' || element.cmtStatus == 'Pending Supplier Action'))
          || element.cmtNumber.includes('SPT-AI-')) {
          hasOpenCase = true;
          this.dialog.alert(this.translate.get('Error'), this.translate.get('Tool can not be saved! Below assets have open case for either asset confirmation or inactive. Please resolve them before saving your changes.'));
        }
      }
    });
    return hasOpenCase;
  }

  /**
  * On removing an assciated part.
  */
  onRemovePart(part: any) {
    const index = this.associatedPartsData.findIndex(x => x.partNumber == part.partNumber);
    if (index > -1) {
      this.associatedPartsData.splice(index, 2);
    }
    this.refreshAssociatedParts();

    const apIndex = this.currentTool.data.associatedParts.findIndex(x => x.partNumber == part.partNumber);
    if (apIndex > -1) {
      this.currentTool.data.associatedParts.splice(apIndex, 1);
    }

    if (this.showCMTChanges == "1") {
      this.setStatusForTeslaAsset();
    }
  }

  setupCmtStatusAfterGettingAssetList(allAssetList: Asset[]) {
    allAssetList.forEach(asset => {
      var matchingasset = this.assets.value.find(x => x.assetCode == asset.assetCode);
      if (matchingasset != null && matchingasset != undefined) {
        var status = this.getCMTStatus(asset.assetCode);
        var cmtNumber = this.getCMTNumber(asset.assetCode);
        if (status != null && status != undefined) {
          this.assets.value.find(x => x.assetCode == asset.assetCode).cmtStatus = status;
          this.assets.value.find(x => x.assetCode == asset.assetCode).cmtNumber = cmtNumber;
          //If asset confirmation creation/update tool is pending
          if (cmtNumber && cmtNumber.includes('SPT-AC-') && (status == 'Pending Tool Create By Supplier')) {
            var currentAssetInfo = this.allAssetList.find(x => x.assetCode == asset.assetCode);
            if (currentAssetInfo != null && currentAssetInfo != undefined) {
              // this.assets.value.find(x => x.assetCode == asset.assetCode).assetStatus = currentAssetInfo.demandStatus.toLocaleLowerCase();
            }
          }
        }
      }
    });

    this.assets.updateValueAndValidity();
    this.currentTool.data.assets = this.assets.value;
    if (this.isTeslaAsset.value == true) {
      this.toolGroup.setControl('assets', this.fb.array(this.populateTeslaAssetsControl(), CustomValidator.arrayRequiredValidator));
    }
    else {
      this.toolGroup.setControl('assets', this.fb.array(this.populateTeslaAssetsControl()));
    }

  }

  // dropdown will be disbled/enabled as follows:
  // 1. If showCMTChanges is OFF, not active status will always be disabled.
  // 2. If showCMTChanges is ON, then
  //      2(a): If an asset is added but no parts are added or already available, then all options will be disabled.
  //      2(b): Since we do not have auto-close for inactive asset CMT, if CMT is open, dropdown will be disabled.
  isActiveDisabled(asset: any) {
    if (this.showCMTChanges == "1") {
      var assetInfo = this.assetSelected.find(x => x.assetCode == asset.get('assetCode').value);
      if (assetInfo != null && assetInfo.assetStatus == 'active') {
        return false;
      }
      if (((asset.get('showActiveStatus') == null || !asset.get('showActiveStatus').value)
        ||
        (asset.get('cmtNumber').value != null && asset.get('cmtNumber').value.includes('SPT-AI-') && asset.get('cmtStatus') != null && asset.get('cmtStatus').value !== 'Closed'))
      ) {
        return true;
      }
    }
  }

  // if all the associated parts have no demand then future will be enabled
  isFutureDisabled(asset: any) {
    if (this.showCMTChanges == "1") {
      var assetInfo = this.assetSelected.find(x => x.assetCode == asset.get('assetCode').value);
      if (assetInfo != null && assetInfo.assetStatus == 'future') {
        return false;
      }
      if (((asset.get('showFutureStatus') == null || !asset.get('showFutureStatus').value)
        ||
        (asset.get('cmtNumber').value != null && asset.get('cmtNumber').value.includes('SPT-AI-') && asset.get('cmtStatus') != null && asset.get('cmtStatus').value !== 'Closed'))
        &&
        ((this.associatedPartsData.length == 0)
          ||
          (this.associatedPartsData.length > 0 && this.associatedPartsData.filter(x => x.demandType && x.demandType.toLocaleLowerCase() == "no demand").length != this.associatedPartsData.length))) {
        return true;
      }
    }
  }

  isServiceDisabled(asset: any) {
    if (this.showCMTChanges == "1") {
      var assetInfo = this.assetSelected.find(x => x.assetCode == asset.get('assetCode').value);
      if (assetInfo != null && assetInfo.assetStatus == 'service') {
        return false;
      }
      if (((asset.get('showServiceStatus') == null || !asset.get('showServiceStatus').value)
        ||
        (asset.get('cmtNumber').value != null && asset.get('cmtNumber').value.includes('SPT-AI-') && asset.get('cmtStatus') != null && asset.get('cmtStatus').value !== 'Closed'))
      ) {
        return true;
      }
    }
  }

  // Inactive asset status will be disabled if:
  // 1) showCMTChanges is false.
  // 2) a new tool is being created.
  isInactiveDisabled(asset: any) {
    if (this.showCMTChanges == "0"
      ||
      (this.showCMTChanges == "1" && ((asset.get('showNoDemandStatus') == null || !asset.get('showNoDemandStatus').value) || !this.currentTool.id))) {
      return true;
    }
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.toolGroup.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }
}

